import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Col, Popconfirm, Row, Space, Spin, Table, Tag, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, RightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Button, ButtonActionBar, EmptyScreen, Navigator } from 'modules/core/components'
import { SiderLayout } from 'modules/core/layouts'
import { ROLES } from 'modules/core/constants'
import { isUserAllowed } from 'modules/core/utils'
import configuration from 'modules/configuration'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import { notifyMappingError } from '../utils'
import { DimensionMappingModal } from './components'

const { SETTINGS__DIMENSION_MAPPINGS__ABM } = ROLES

const DimensionMappingsList = ({
  dimensionMappingsList,
  fetchDimensionMappings,
  deleteDimensionMapping,
  fetchAllDimensions,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editMapping, setEditMapping] = useState(null)
  const [deleteKey, setDeleteKey] = useState(null)
  const [dimensions, setDimensions] = useState([])
  const history = useHistory()
  const { t } = useTranslation()

  const userCanABM = isUserAllowed(SETTINGS__DIMENSION_MAPPINGS__ABM)

  useEffect(() => {
    setIsLoading(true)
    fetchDimensionMappings().then(() => setIsLoading(false))
  }, [fetchDimensionMappings])

  // El catálogo solo lo necesita el modal de alta.
  useEffect(() => {
    if (!userCanABM) return
    fetchAllDimensions().then((response) => setDimensions(response.payload.data))
  }, [fetchAllDimensions, userCanABM])

  const handleDelete = (sourceDimension) => {
    setIsSaving(true)
    deleteDimensionMapping(sourceDimension)
      .then(() => fetchDimensionMappings())
      .catch(notifyMappingError)
      .finally(() => {
        setIsSaving(false)
        setDeleteKey(null)
      })
  }

  const renderHeader = () => (
    <Navigator
      floated={false}
      size="big"
      items={[
        { name: t('LABEL_CONFIG'), url: '/configuracion' },
        { name: t('CONFIG_DIMENSION_MAPPINGS') },
      ]}
    />
  )

  const columns = [
    {
      dataIndex: 'name',
      title: t('FIELD_NAME'),
    },
    {
      dataIndex: 'source_dimension',
      title: t('CONFIG_DIMENSION_MAPPING_FIELD_SOURCE'),
      render: (sourceDimension, row) => row.labels?.[sourceDimension] ?? sourceDimension,
    },
    {
      dataIndex: 'targets',
      title: t('CONFIG_DIMENSION_MAPPING_FIELD_TARGETS'),
      render: (targets, row) =>
        (targets ?? []).map((target) => row.labels?.[target] ?? target).join(', '),
    },
    {
      dataIndex: 'source_system',
      title: t('CONFIG_DIMENSION_MAPPING_FIELD_ADMINISTRATION'),
      render: (sourceSystem) =>
        sourceSystem ? (
          <Tooltip title={t('CONFIG_DIMENSION_MAPPING_MANAGED_EXTERNALLY_TOOLTIP')}>
            <Tag color="blue">
              {t('CONFIG_DIMENSION_MAPPING_MANAGED_EXTERNALLY', { system: sourceSystem })}
            </Tag>
          </Tooltip>
        ) : (
          t('CONFIG_DIMENSION_MAPPING_ADMINISTRATION_PLIKA')
        ),
    },
    {
      dataIndex: 'source_dimension',
      key: 'actions',
      title: t('FIELD_ACTIONS'),
      align: 'right',
      render: (sourceDimension, row) => (
        <Space size="middle">
          {/* La edición también habilita los de solo lectura: es como se revierte el origen externo. */}
          {userCanABM && (
            <Button.Icon
              title="ACTION_EDIT"
              icon={<EditOutlined />}
              onClick={() => {
                setEditMapping(row)
                setShowModal(true)
              }}
            />
          )}
          {/* El mapeo lo declara Plika, así que también lo borra: solo las relaciones son ajenas. */}
          {userCanABM && (
            <Popconfirm
              placement="bottomRight"
              title={t(
                row.is_editable
                  ? 'CONFIG_DIMENSION_MAPPING_DELETE_CONFIRMATION'
                  : 'CONFIG_DIMENSION_MAPPING_DELETE_CONFIRMATION_EXTERNAL',
                { count: row.rows_count ?? 0, system: row.source_system },
              )}
              okText={t('ACTION_DELETE')}
              cancelText={t('ACTION_CANCEL')}
              okButtonProps={{ loading: isSaving }}
              cancelButtonProps={{ disabled: isSaving }}
              onConfirm={() => handleDelete(sourceDimension)}
              onCancel={() => setDeleteKey(null)}
              visible={deleteKey === sourceDimension}
            >
              <Button.Icon
                title="ACTION_DELETE"
                icon={<DeleteOutlined />}
                onClick={() => setDeleteKey(sourceDimension)}
              />
            </Popconfirm>
          )}
          <RightOutlined
            className="enter-icon"
            onClick={() => history.push(`/configuracion/mapeos/${sourceDimension}`)}
          />
        </Space>
      ),
    },
  ]

  const renderNoDataScreen = () => (
    <EmptyScreen
      image={emptyStateImage}
      description={t('CONFIG_DIMENSION_MAPPINGS_EMPTY_DESCRIPTION')}
      footer={
        userCanABM && (
          <Button.Primary
            title="CONFIG_DIMENSION_MAPPINGS_CREATE_ACTION"
            onClick={() => setShowModal(true)}
          />
        )
      }
    />
  )

  return (
    <SiderLayout>
      <Row gutter={[12, 12]}>
        <Col span={24}>{renderHeader()}</Col>
        <Col span={24}>
          <Spin spinning={isLoading}>
            {dimensionMappingsList.length === 0 && !isLoading && renderNoDataScreen()}
            {dimensionMappingsList.length > 0 && (
              <Row gutter={[24, 24]}>
                {userCanABM && (
                  <Col span={24}>
                    <ButtonActionBar>
                      <Button.Primary
                        title="CONFIG_DIMENSION_MAPPINGS_CREATE_ACTION"
                        onClick={() => setShowModal(true)}
                      />
                    </ButtonActionBar>
                  </Col>
                )}
                <Col span={24}>
                  <Table
                    size="small"
                    rowKey="source_dimension"
                    columns={columns}
                    dataSource={dimensionMappingsList}
                    loading={isSaving}
                    pagination={false}
                    onRow={(row) => ({
                      onDoubleClick: () =>
                        history.push(`/configuracion/mapeos/${row.source_dimension}`),
                    })}
                  />
                </Col>
              </Row>
            )}
          </Spin>
        </Col>
      </Row>
      <DimensionMappingModal
        visible={showModal}
        mapping={editMapping}
        onClose={() => {
          setShowModal(false)
          setEditMapping(null)
        }}
        onRefetch={fetchDimensionMappings}
        dimensions={dimensions}
        usedDimensions={dimensionMappingsList.map((mapping) => mapping.source_dimension)}
      />
    </SiderLayout>
  )
}

const mapStateToProps = (state) => ({
  dimensionMappingsList: configuration.selectors.getDimensionMappingsList(state),
})

const mapDispatchToProps = {
  fetchDimensionMappings: configuration.actions.fetchDimensionMappings,
  deleteDimensionMapping: configuration.actions.deleteDimensionMapping,
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
}

export default connect(mapStateToProps, mapDispatchToProps)(DimensionMappingsList)
